import type { InteractionMode } from '../core/InteractionMode.js';
import { VISUAL_RECIPE_VERSION, layer, type VisualRecipe } from '../recipe/VisualRecipe.js';

/**
 * What each interaction mode does to the picture.
 *
 * The modes were declared in Phase 1 and had no behaviour; this is the file that
 * gives them some. Each returns a recipe whose *mappings* differ — not its
 * renderers, and certainly not its judging. Nothing here touches the simulation,
 * so switching mode mid-run cannot alter a score.
 *
 * The three differ in what they consider worth reacting to:
 *
 *   SCORE  reacts to the *result* of playing. Hits bloom, misses distort, combo
 *          grows the field. A player who has come for the rhythm game gets a
 *          picture that answers how they are doing.
 *
 *   ART    reacts to the *act* of playing. Every press is material — including
 *          the ones that judged nothing, which is what `PLAYER_INPUT -> SHIFT`
 *          buys: strike where there is no note and a spiral appears anyway.
 *          Misses are mapped to `SPLASH` rather than to anything dimming.
 *
 *   AUTO   reacts to the music alone. No input signal appears in its mappings at
 *          all, so an untouched session still produces a moving piece.
 */

export interface PresetOptions {
  readonly seed: number;
  /** Renderer types this build has, so a preset never asks for a missing one. */
  readonly available: readonly string[];
}

export function presetFor(mode: InteractionMode, options: PresetOptions): VisualRecipe {
  switch (mode) {
    case 'ART':
      return artPreset(options);
    case 'AUTO':
      return autoPreset(options);
    default:
      return scorePreset(options);
  }
}

/** The rhythm game, with the picture answering the run. */
function scorePreset({ seed, available }: PresetOptions): VisualRecipe {
  const layers = [
    layer({
      id: 'mandala',
      renderer: 'MANDALA',
      zDepth: 0,
      seed,
      signalMapping: {
        NOTE_HIT: { action: 'SPAWN', strength: 0.5 },
        NOTE_MISS: { action: 'DISTORT', strength: 0.7 },
        BASS: { target: 'bloomTrim', scale: 0.3, offset: 0.95 },
      },
    }),
  ];
  if (available.includes('RIPPLE')) {
    layers.push(
      layer({
        id: 'ripple',
        renderer: 'RIPPLE',
        zDepth: 1,
        seed: seed + 1,
        blendMode: 'add',
        opacity: 0.85,
        parameters: { spawnRate: 1.2, growth: 0.5, lifetime: 1.8, hue: 0.52 },
        signalMapping: {
          NOTE_HIT: { action: 'SPAWN', strength: 0.9 },
          NOTE_MISS: { action: 'DISTORT', strength: 1 },
          COMBO: { action: 'EXPAND', scale: 0.02, curve: 'sqrt' },
          BAR: { action: 'ROTATE', strength: 0.5 },
        },
      }),
    );
  }
  return { version: VISUAL_RECIPE_VERSION, seed, renderMode: 'FIXED', layers };
}

/**
 * Playing as intervention.
 *
 * The mappings that matter are `PLAYER_INPUT -> SHIFT` and `NOTE_MISS ->
 * SPLASH`. The first is what makes an off-beat press produce a mark; the second
 * is what makes a miss produce a *different* mark rather than a worse one.
 */
function artPreset({ seed, available }: PresetOptions): VisualRecipe {
  const layers = [
    layer({
      id: 'mandala',
      renderer: 'MANDALA',
      zDepth: 0,
      seed,
      intensity: 1.05,
      signalMapping: {
        NOTE_MISS: { action: 'DISTORT', strength: 1 },
        PLAYER_INPUT: { action: 'SHIFT', strength: 0.4 },
        AMPLITUDE: { target: 'bloomTrim', scale: 0.35, offset: 0.95 },
      },
    }),
  ];
  if (available.includes('RIPPLE')) {
    layers.push(
      layer({
        id: 'ripple',
        renderer: 'RIPPLE',
        zDepth: 1,
        seed: seed + 1,
        blendMode: 'add',
        opacity: 0.95,
        intensity: 1.1,
        parameters: { spawnRate: 0.8, growth: 0.55, lifetime: 2.2, distortion: 0.3, hue: 0.48 },
        signalMapping: {
          NOTE_HIT: { action: 'SPAWN', strength: 0.85 },
          // Splash, not fade. A miss scatters ink.
          NOTE_MISS: { action: 'SPLASH', strength: 1 },
          // The off-beat rule: a press with nothing under it still marks.
          PLAYER_INPUT: { action: 'SHIFT', strength: 0.75 },
          TREBLE: { action: 'TURBULENCE', scale: 0.5 },
        },
      }),
    );
  }
  if (available.includes('MATH')) {
    layers.push(
      layer({
        id: 'math',
        renderer: 'MATH',
        zDepth: 2,
        seed: seed + 2,
        blendMode: 'add',
        opacity: 0.5,
        parameters: { scale: 0.55, offsetX: -0.2, offsetY: 0.62 },
        signalMapping: {
          NOTE_MISS: { action: 'BREAK', strength: 1 },
          PLAYER_INPUT: { action: 'SHIFT', strength: 0.5 },
          BAR: { action: 'ROTATE', strength: 0.6 },
        },
      }),
    );
  }
  return { version: VISUAL_RECIPE_VERSION, seed, renderMode: 'FIXED', layers };
}

/** Nobody is playing. Everything comes from the audio. */
function autoPreset({ seed, available }: PresetOptions): VisualRecipe {
  const layers = [
    layer({
      id: 'mandala',
      renderer: 'MANDALA',
      zDepth: 0,
      seed,
      signalMapping: {
        BASS: { target: 'bloomTrim', scale: 0.35, offset: 0.95 },
        BAR: { action: 'SHIFT', strength: 0.35 },
      },
    }),
  ];
  if (available.includes('RIPPLE')) {
    layers.push(
      layer({
        id: 'ripple',
        renderer: 'RIPPLE',
        zDepth: 1,
        seed: seed + 1,
        blendMode: 'add',
        opacity: 0.85,
        parameters: { spawnRate: 1.6, growth: 0.5, lifetime: 2, distortion: 0.35, hue: 0.55 },
        signalMapping: {
          // No input signals at all: this is the whole point of the mode.
          ONSET: { action: 'SPAWN', strength: 0.75 },
          BASS: { action: 'DENSITY', scale: 0.8 },
          TREBLE: { action: 'TURBULENCE', scale: 0.45 },
          BAR: { action: 'ROTATE', strength: 0.6 },
        },
      }),
    );
  }
  return { version: VISUAL_RECIPE_VERSION, seed, renderMode: 'FIXED', layers };
}
